import { useEffect, useId, useRef, type ReactNode } from 'react';
import { IconButton } from './Button.js';
import { cx } from './cx.js';
import { CloseIcon } from './icons.js';

/**
 * Feuille : sur téléphone, monte du bas de l'écran (à portée du pouce) ; sur ordinateur, panneau
 * latéral à droite (`side`) ou fenêtre centrée (`center`). Repose sur <dialog> natif : focus piégé,
 * Échap pour fermer, fond assombri, retour du focus au déclencheur.
 *
 * À réserver aux tâches qui demandent un focus protégé (choix, saisie courte). Pour une simple
 * confirmation, préférer une confirmation en place (ConfirmAction).
 *
 *   <Sheet open={open} onClose={() => setOpen(false)} title="Choisir un emplacement" footer={<Button …/>}>
 *     …
 *   </Sheet>
 */
export function Sheet({
  open,
  onClose,
  title,
  description,
  footer,
  desktop = 'side',
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  desktop?: 'side' | 'center';
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
    } else if (!open && d.open) {
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // Appui sur le fond (hors panneau) : fermeture.
        if (e.target === e.currentTarget) onClose();
      }}
      className={cx(
        'ui-sheet fixed inset-0 h-dvh w-screen',
        open ? 'flex' : 'hidden',
        'items-end justify-center',
        desktop === 'side' ? 'lg:items-stretch lg:justify-end' : 'lg:items-center',
      )}
    >
      <div
        className={cx(
          'ui-sheet-panel flex max-h-[92dvh] w-full flex-col bg-surface shadow-lg',
          'rounded-t-3xl pb-[var(--safe-bottom)]',
          desktop === 'side'
            ? 'lg:max-h-none lg:w-[440px] lg:rounded-none lg:rounded-l-3xl lg:pb-0'
            : 'lg:max-h-[85dvh] lg:w-[520px] lg:rounded-3xl lg:pb-0',
        )}
      >
        <div aria-hidden className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-line-strong lg:hidden" />
        <header className="flex items-start justify-between gap-3 px-5 pt-3 pb-2 lg:pt-5">
          <div className="min-w-0 pt-1.5">
            <h2 id={titleId} className="text-lg font-semibold text-ink">
              {title}
            </h2>
            {description ? (
              <p id={descId} className="mt-0.5 text-sm text-ink-3">
                {description}
              </p>
            ) : null}
          </div>
          <IconButton label="Fermer" onClick={onClose} className="-mr-2">
            <CloseIcon />
          </IconButton>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">{children}</div>
        {footer ? <footer className="border-t border-line bg-surface px-5 py-3">{footer}</footer> : null}
      </div>
    </dialog>
  );
}
